// Privacy Shield — composer preview chip + admin policy card.
// Module scope (not inside App) so App re-renders never remount them.
import { useEffect, useRef, useState } from "react";

// Last preview result, shared with send() so a message the org blocks is
// stopped client-side too (the server enforces it regardless).
let _lastPreview = { text: null, blocked: [], labels: {} };
export function privacyShieldBlocked(text) {
  const t = String(text || "").trim();
  if (_lastPreview.text !== t || !_lastPreview.blocked.length) return null;
  return _lastPreview.blocked.map((k) => _lastPreview.labels[k] || k);
}

export function summarizeFindings(summary, labels, blocked) {
  const parts = [];
  for (const [k, n] of Object.entries(summary || {})) {
    if (blocked && blocked.includes(k)) continue;
    const label = (labels && labels[k]) || k;
    // Custom rule names are nouns we can't pluralise reliably: "Client names (2)".
    parts.push(/^C_/.test(k) ? `${label} (${n})` : `${n} ${label}${n === 1 || /s$/.test(label) ? "" : "s"}`);
  }
  return parts.join(", ");
}

let _disabledUntil = 0;

export function PrivacyShieldChip({ textareaRef, value, authToken, active, S }) {
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const lastText = useRef(null);
  const timer = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!active || !authToken) return undefined;
    const read = () => (value !== undefined ? value : (textareaRef && textareaRef.current ? textareaRef.current.value : ""));
    const check = () => {
      const text = String(read() || "").trim();
      if (text === lastText.current) return;
      lastText.current = text;
      clearTimeout(timer.current);
      if (text.length < 4 || Date.now() < _disabledUntil) {
        setRes(null);
        _lastPreview = { text: null, blocked: [], labels: {} };
        return;
      }
      timer.current = setTimeout(async () => {
        const my = ++seq.current;
        try {
          const r = await fetch("/api/redaction/preview", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({ text }),
          });
          if (!r.ok || my !== seq.current) return;
          const d = await r.json();
          if (!d.enabled) { _disabledUntil = Date.now() + 5 * 60_000; setRes(null); return; }
          _lastPreview = { text, blocked: d.blocked || [], labels: d.labels || {} };
          setRes(Object.keys(d.summary || {}).length ? d : null);
        } catch {}
      }, 600);
    };
    check();
    const iv = setInterval(check, 500);
    return () => { clearInterval(iv); clearTimeout(timer.current); };
  }, [active, authToken, value, textareaRef]);

  if (!res) return null;
  const blocked = res.blocked || [];
  const blockedNames = blocked.map((k) => (res.labels && res.labels[k]) || k);
  const masked = summarizeFindings(res.summary, res.labels, blocked);
  const danger = blocked.length > 0;
  const col = danger ? S.danger : (S.success || "#10b981");
  return (
    <div className="cf-privacy-chip" style={{ margin: "0 14px 6px", fontSize: 11.5 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 10px", borderRadius: 10, background: col + "14", border: `1px solid ${col}40`, color: S.text2, flexWrap: "wrap" }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2.2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
        <span style={{ flex: 1, minWidth: 140 }}>
          {danger
            ? <><strong style={{ color: col }}>Blocked by your organization:</strong> {blockedNames.join(", ")}. Remove it to send.</>
            : <><strong style={{ color: col }}>Privacy Shield</strong> will mask {masked} before this reaches the AI.</>}
        </span>
        <button onClick={() => setOpen((o) => !o)} style={{ background: "none", border: "none", color: S.accent, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit", padding: 0 }}>
          {open ? "Hide" : "Preview"}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 5, padding: "8px 10px", borderRadius: 8, background: S.codeBlockBg || S.inputBg, border: `1px solid ${S.border2}`, maxHeight: 160, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12, color: S.text2, lineHeight: 1.5 }}>
          <div style={{ fontSize: 10, color: S.text3, marginBottom: 4, fontFamily: "monospace" }}>WHAT THE AI WILL SEE</div>
          {res.masked}
        </div>
      )}
    </div>
  );
}

// ── Admin card ─────────────────────────────────────────────────────────────
const KIND_DESC = {
  SSN: { label: "SSN", desc: "U.S. Social Security numbers (XXX-XX-XXXX)" },
  EMAIL: { label: "Email", desc: "Email addresses" },
  PHONE: { label: "Phone", desc: "North-American phone numbers" },
  CARD: { label: "Credit card", desc: "13–19 digit numbers that pass Luhn" },
  DOB: { label: "Date of birth", desc: "MM/DD/YYYY, ISO, or DOB:-prefixed" },
  MRN: { label: "MRN", desc: "Medical record numbers prefixed by MRN:" },
  IP: { label: "IP address", desc: "IPv4 addresses" },
  NAME: { label: "Names (heuristic)", desc: "Title-Case word pairs — high false-positive rate", warn: true },
};
const blankRule = () => ({ label: "", type: "terms", value: "", action: "mask", caseSensitive: false });

export function PrivacyShieldAdmin({ authToken, showToast, S }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rules, setRules] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [stats, setStats] = useState(null);
  const [trial, setTrial] = useState("");
  const [trialRes, setTrialRes] = useState(null);
  const H = { Authorization: `Bearer ${authToken}` };

  const load = async () => {
    try {
      const r = await fetch("/api/admin/redaction", { headers: H });
      if (r.ok) {
        const d = await r.json();
        setSt(d);
        setRules((d.customRules || []).map((x) => ({ ...x, value: Array.isArray(x.value) ? x.value.join(", ") : x.value })));
        setDirty(false);
      }
    } catch {}
    try { const r = await fetch("/api/admin/redaction/stats", { headers: H }); if (r.ok) setStats(await r.json()); } catch {}
  };
  useEffect(() => { if (authToken) load(); }, [authToken]);

  const save = async (patch, msg) => {
    if (!st) return false;
    setBusy(true);
    const next = { ...st, ...patch };
    const body = {
      enabled: next.enabled, classes: next.classes, blockClasses: next.blockClasses || [],
      persistMaskedInStorage: next.persistMaskedInStorage,
      customRules: (patch.customRules || st.customRules || []).map((r) => ({ ...r })),
    };
    try {
      const r = await fetch("/api/admin/redaction", { method: "PUT", headers: { ...H, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { showToast(d.error || "Save failed"); setBusy(false); return false; }
      setSt(d);
      showToast(msg || "Privacy Shield updated");
      setBusy(false);
      return true;
    } catch (e) { showToast("Save failed: " + e.message); setBusy(false); return false; }
  };
  if (!st || !rules) return null;

  const actionOf = (k) => (st.blockClasses || []).includes(k) ? "block" : st.classes.includes(k) ? "mask" : "off";
  const setAction = (k, a) => {
    const classes = st.classes.filter((c) => c !== k);
    const blockClasses = (st.blockClasses || []).filter((c) => c !== k);
    if (a === "mask") classes.push(k);
    if (a === "block") blockClasses.push(k);
    save({ classes, blockClasses });
  };
  const saveRules = async () => {
    const ok = await save({ customRules: rules.filter((r) => r.label.trim() || String(r.value).trim()) }, "Custom rules saved");
    if (ok) { setDirty(false); load(); }
  };
  const runTrial = async () => {
    if (!trial.trim()) return;
    try {
      const r = await fetch("/api/redaction/preview", { method: "POST", headers: { ...H, "Content-Type": "application/json" }, body: JSON.stringify({ text: trial }) });
      setTrialRes(r.ok ? await r.json() : { error: "Preview failed" });
    } catch (e) { setTrialRes({ error: e.message }); }
  };

  const seg = (k) => (
    <div style={{ display: "flex", borderRadius: 7, overflow: "hidden", border: `1px solid ${S.border2}`, flexShrink: 0 }}>
      {["off", "mask", "block"].map((a) => {
        const on = actionOf(k) === a;
        const col = a === "block" ? S.danger : a === "mask" ? (S.success || "#10b981") : S.text3;
        return (
          <button key={a} disabled={busy} onClick={() => setAction(k, a)}
            style={{ padding: "3px 8px", fontSize: 10, fontWeight: 700, border: "none", cursor: busy ? "default" : "pointer", fontFamily: "inherit", background: on ? col + "22" : "transparent", color: on ? col : S.text3, textTransform: "capitalize" }}>{a}</button>
        );
      })}
    </div>
  );
  const input = { background: S.inputBg, border: `1px solid ${S.border2}`, borderRadius: 7, padding: "6px 9px", color: S.text, fontSize: 12, outline: "none", fontFamily: "inherit" };
  const totals = stats && stats.totals;
  const totalMasked = totals ? Object.values(totals.masked).reduce((a, b) => a + b, 0) : 0;
  const maxDay = stats ? Math.max(1, ...stats.days.map((d) => d.requests || 0)) : 1;

  return (
    <div className="cf-privacy-admin" style={{ background: S.cardBg, border: `1px solid ${S.border2}`, borderLeft: `3px solid ${S.success}`, borderRadius: 14, overflow: "hidden", marginBottom: 14 }}>
      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${S.border}`, fontSize: 12, fontWeight: 700, color: S.text2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>🛡 Privacy Shield</span>
        <span style={{ fontSize: 10, fontFamily: "monospace", padding: "2px 8px", borderRadius: 10, background: st.enabled ? S.success + "18" : S.text3 + "18", color: st.enabled ? S.success : S.text3 }}>{st.enabled ? "active" : "off"}</span>
      </div>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontSize: 11, color: S.text3, lineHeight: 1.6 }}>
          Sensitive data is detected before anything reaches an AI model. <b>Mask</b> swaps it for a placeholder (the reply is unmasked for display); <b>Block</b> stops the message entirely. Members see what will happen as they type.
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 10, border: `1px solid ${st.enabled ? S.accent : S.border2}`, background: st.enabled ? S.accentDim : "transparent" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: st.enabled ? S.accent : S.text }}>Protect messages before they reach AI</div>
          <div onClick={() => !busy && save({ enabled: !st.enabled })} role="switch" aria-checked={st.enabled} style={{ width: 40, height: 22, background: st.enabled ? S.success : S.surface3, borderRadius: 12, position: "relative", cursor: "pointer", border: `1px solid ${S.border2}`, flexShrink: 0 }}>
            <div style={{ position: "absolute", top: 2, left: st.enabled ? 20 : 2, width: 16, height: 16, background: "#fff", borderRadius: "50%", transition: "left 0.18s" }} />
          </div>
        </div>

        <div style={{ opacity: st.enabled ? 1 : 0.55, pointerEvents: st.enabled ? "auto" : "none", display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={{ fontSize: 10, fontFamily: "monospace", color: S.text3, textTransform: "uppercase", marginBottom: 6 }}>Built-in detectors</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {(st.available || Object.keys(KIND_DESC)).map((k) => {
                const d = KIND_DESC[k] || { label: k, desc: "" };
                return (
                  <div key={k} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", borderRadius: 8, background: S.inputBg, border: `1px solid ${S.border2}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: S.text }}>{d.label}{d.warn && <span style={{ fontSize: 9, color: S.warning || "#f59e0b", marginLeft: 6 }}>noisy</span>}</div>
                      <div style={{ fontSize: 10.5, color: S.text3 }}>{d.desc}</div>
                    </div>
                    {seg(k)}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 10, fontFamily: "monospace", color: S.text3, textTransform: "uppercase", marginBottom: 6 }}>Your organization's rules</div>
            <div style={{ fontSize: 10.5, color: S.text3, marginBottom: 8, lineHeight: 1.5 }}>Add words (client names, project code names) or a pattern (e.g. matter numbers <code>M-\d{"{6}"}</code>). Patterns with nested repetition are rejected for safety.</div>
            {rules.map((r, i) => (
              <div key={i} style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "flex-start", padding: 8, borderRadius: 8, border: `1px solid ${S.border2}`, marginBottom: 6 }}>
                <input aria-label="Rule name" placeholder="Name (e.g. Client names)" value={r.label} onChange={(e) => { const v = e.target.value; setRules((p) => p.map((x, j) => j === i ? { ...x, label: v } : x)); setDirty(true); }} style={{ ...input, flex: "1 1 140px" }} />
                <select aria-label="Rule type" value={r.type} onChange={(e) => { const v = e.target.value; setRules((p) => p.map((x, j) => j === i ? { ...x, type: v } : x)); setDirty(true); }} style={{ ...input, cursor: "pointer" }}>
                  <option value="terms">Words / phrases</option>
                  <option value="pattern">Pattern (regex)</option>
                </select>
                <select aria-label="Rule action" value={r.action} onChange={(e) => { const v = e.target.value; setRules((p) => p.map((x, j) => j === i ? { ...x, action: v } : x)); setDirty(true); }} style={{ ...input, cursor: "pointer" }}>
                  <option value="mask">Mask</option>
                  <option value="block">Block</option>
                </select>
                <button onClick={() => { setRules((p) => p.filter((_, j) => j !== i)); setDirty(true); }} title="Remove rule" style={{ ...input, cursor: "pointer", color: S.danger, borderColor: S.danger + "40" }}>×</button>
                {r.type === "terms"
                  ? <textarea aria-label="Words" placeholder="Acme Corp, Project Falcon, …" value={r.value} onChange={(e) => { const v = e.target.value; setRules((p) => p.map((x, j) => j === i ? { ...x, value: v } : x)); setDirty(true); }} rows={2} style={{ ...input, flexBasis: "100%", resize: "vertical" }} />
                  : <input aria-label="Pattern" placeholder="\bM-\d{6}\b" value={r.value} onChange={(e) => { const v = e.target.value; setRules((p) => p.map((x, j) => j === i ? { ...x, value: v } : x)); setDirty(true); }} style={{ ...input, flexBasis: "100%", fontFamily: "monospace" }} />}
                <label style={{ fontSize: 10.5, color: S.text3, display: "flex", alignItems: "center", gap: 5 }}>
                  <input type="checkbox" checked={!!r.caseSensitive} onChange={(e) => { const v = e.target.checked; setRules((p) => p.map((x, j) => j === i ? { ...x, caseSensitive: v } : x)); setDirty(true); }} /> Case-sensitive
                </label>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setRules((p) => [...p, blankRule()]); setDirty(true); }} style={{ ...input, cursor: "pointer", color: S.accent }}>+ Add rule</button>
              {dirty && <button onClick={saveRules} disabled={busy} style={{ ...input, cursor: "pointer", background: S.accent, color: "#fff", border: "none", fontWeight: 700 }}>Save rules</button>}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 10, fontFamily: "monospace", color: S.text3, textTransform: "uppercase", marginBottom: 6 }}>Try it</div>
            <div style={{ display: "flex", gap: 6 }}>
              <textarea aria-label="Test message" value={trial} onChange={(e) => setTrial(e.target.value)} rows={2} placeholder="Paste a sample message to see what the AI would receive (uses saved settings)" style={{ ...input, flex: 1, resize: "vertical" }} />
              <button onClick={runTrial} style={{ ...input, cursor: "pointer", color: S.accent, alignSelf: "flex-start" }}>Test</button>
            </div>
            {trialRes && (
              <div style={{ marginTop: 6, padding: "8px 10px", borderRadius: 8, background: S.inputBg, border: `1px solid ${(trialRes.blocked || []).length ? S.danger : S.border2}`, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", color: S.text2 }}>
                {trialRes.error ? trialRes.error
                  : !trialRes.enabled ? "Privacy Shield is off."
                  : (trialRes.blocked || []).length ? `⛔ Would be blocked: ${trialRes.blocked.map((k) => trialRes.labels[k] || k).join(", ")}`
                  : trialRes.masked}
              </div>
            )}
          </div>
        </div>

        {totals && (
          <div>
            <div style={{ fontSize: 10, fontFamily: "monospace", color: S.text3, textTransform: "uppercase", marginBottom: 6 }}>Last 30 days</div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 8 }}>
              <div><div style={{ fontSize: 18, fontWeight: 800, color: S.text }}>{totalMasked}</div><div style={{ fontSize: 10, color: S.text3 }}>items masked</div></div>
              <div><div style={{ fontSize: 18, fontWeight: 800, color: S.text }}>{totals.requests}</div><div style={{ fontSize: 10, color: S.text3 }}>messages protected</div></div>
              <div><div style={{ fontSize: 18, fontWeight: 800, color: totals.blockedRequests ? S.danger : S.text }}>{totals.blockedRequests}</div><div style={{ fontSize: 10, color: S.text3 }}>messages blocked</div></div>
            </div>
            <div aria-label="Messages with sensitive data per day" style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 36, marginBottom: 8 }}>
              {stats.days.map((d) => (
                <div key={d.day} title={`${d.day}: ${d.requests} message(s)${d.blockedRequests ? `, ${d.blockedRequests} blocked` : ""}`}
                  style={{ flex: 1, minWidth: 2, height: `${Math.max(2, (d.requests / maxDay) * 36)}px`, borderRadius: 2, background: d.blockedRequests ? S.danger : (d.requests ? S.accent : S.border2) }} />
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {Object.entries(totals.masked).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                <span key={k} style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 10, background: S.inputBg, border: `1px solid ${S.border2}`, color: S.text2 }}>{(stats.labels && stats.labels[k]) || k}: {n}</span>
              ))}
              {Object.entries(totals.blocked).map(([k, n]) => (
                <span key={"b" + k} style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 10, background: S.danger + "12", border: `1px solid ${S.danger}40`, color: S.danger }}>blocked {(stats.labels && stats.labels[k]) || k}: {n}</span>
              ))}
            </div>
          </div>
        )}

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: S.text2 }}>
          <input type="checkbox" checked={st.persistMaskedInStorage !== false} onChange={(e) => save({ persistMaskedInStorage: e.target.checked })} />
          Store only the masked version of messages in chat history
        </label>
      </div>
    </div>
  );
}
